import { useState } from "react";
import { Link } from "react-router-dom";
import NeutralHeader from "@/components/NeutralHeader";
import MinimalFooter from "@/components/MinimalFooter";
import { useSeo } from "@/lib/seo";
import { ChevronDown, Headphones, ArrowRight } from "lucide-react";

const COUPLE_IMG =
  "https://images.unsplash.com/photo-1508963493744-76fce69379c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const FAQS = [
  {
    q: "What is the Financial Future Readiness Assessment?",
    a: "It's a short, five-part assessment that gives you a clearer picture of your overall financial readiness. You'll answer a series of questions about your savings, investments, taxes, protection, future income planning, and legacy goals. At the end, you'll receive a personalized readiness score and insights to help you understand areas that are going well and areas that may deserve a closer look.",
  },
  {
    q: "How long does the assessment take?",
    a: "Most people complete the assessment in about three to five minutes. You can take your time — there is no timer, and you can go back to previous questions at any point.",
  },
  {
    q: "Do I need to create an account?",
    a: "No. You can complete the assessment and view your score without providing contact information. If you choose to request a plan review or schedule a Discovery Meeting after seeing your results, you can submit your contact information at that point.",
  },
  {
    q: "Is my information private and secure?",
    a: "The application uses reasonable technical safeguards such as secure transmission, protected backend access, server-side validation, and restricted administrative access. Please do not enter Social Security numbers, account numbers, passwords, or tax identification numbers — the assessment does not need them. No online system can be described as absolutely secure, so please avoid entering highly sensitive information.",
  },
  {
    q: "What does my readiness score mean?",
    a: "The score is an educational 0–100 value based on your responses across five planning areas: Tax Planning, Future Income & Retirement Planning, Asset Management, Insurance & Protection, and Legacy & Estate Planning. It is designed to highlight areas that appear well organized and areas that may deserve a closer look. It is not a financial plan, a prediction, or a recommendation.",
  },
  {
    q: "Does this site provide financial advice?",
    a: "No. The assessment is educational and informational only. It does not provide individualized investment, tax, legal, or insurance advice. For personalized guidance, consider speaking with a qualified financial, tax, or legal professional.",
  },
  {
    q: "Does a higher net worth automatically mean a higher score?",
    a: "No. The amount of investable assets is used as context, not as a scoring input. Someone with a very high asset amount and disorganized planning can score lower than someone with modest assets and coordinated planning.",
  },
  {
    q: "What happens after I get my score?",
    a: "You can simply review your score and educational results, or — if you would like — optionally request a plan review or schedule a Discovery Meeting with the planning team. There is no obligation to take a next step.",
  },
];

export default function FAQ() {
  const [openIdx, setOpenIdx] = useState(0);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  useSeo({
    title: "Financial Readiness Assessment FAQ",
    description: "Answers to common questions about the Financial Future Readiness Assessment, score, privacy, data, and how results are calculated.",
    canonicalPath: "/financial-readiness-faq",
    jsonLd,
    jsonLdId: "faq",
  });

  return (
    <div className="min-h-screen flex flex-col">
      <NeutralHeader />
      <main id="main-content" tabIndex={-1} className="flex-1" data-testid="faq-main">

        {/* HERO */}
        <section className="atmo-mountain">
          <div className="summit-container py-14 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="section-pill mb-5" data-testid="faq-eyebrow">FAQ</div>
              <h1
                className="font-serif"
                style={{ color: "#1E1968", fontSize: "clamp(2.2rem, 4.5vw, 3.5rem)", fontWeight: 600, lineHeight: 1.05, letterSpacing: "-0.015em" }}
                data-testid="faq-title"
              >
                Frequently Asked<br />Questions
              </h1>
              <p className="mt-5 text-lg" style={{ color: "#17233D", maxWidth: 560, lineHeight: 1.55 }}>
                Find answers to common questions about the Financial Future Readiness Assessment.
                If you don&rsquo;t see what you&rsquo;re looking for, we&rsquo;re here to help.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div style={{ background: "#ffffff", padding: 8, boxShadow: "0 10px 28px rgba(23, 35, 61, 0.08)" }}>
                <img src={COUPLE_IMG} alt="" loading="eager" className="w-full h-[240px] md:h-[320px] object-cover" style={{ display: "block" }} />
              </div>
            </div>
          </div>
        </section>

        {/* ACCORDION */}
        <section style={{ background: "#ffffff" }}>
          <div className="summit-container py-14 lg:py-16" style={{ maxWidth: 1040 }}>
            <div data-testid="faq-list">
              {FAQS.map((f, i) => {
                const open = openIdx === i;
                const panelId = `faq-panel-${i}`;
                const triggerId = `faq-trigger-${i}`;
                return (
                  <div key={i} className="faq-item" data-testid={`faq-item-${i}`}>
                    <button
                      type="button"
                      id={triggerId}
                      className="faq-trigger"
                      aria-expanded={open}
                      aria-controls={panelId}
                      onClick={() => setOpenIdx(open ? -1 : i)}
                      data-testid={`faq-trigger-${i}`}
                    >
                      <span>{f.q}</span>
                      <ChevronDown
                        aria-hidden="true"
                        style={{
                          flex: "0 0 auto", width: 20, height: 20, color: "#258FC4",
                          transform: open ? "rotate(180deg)" : "none",
                          transition: "transform 0.18s ease",
                        }}
                        strokeWidth={2}
                      />
                    </button>
                    {open && (
                      <div id={panelId} role="region" aria-labelledby={triggerId} className="faq-panel" data-testid={`faq-panel-${i}`}>
                        <p>{f.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* STILL HAVE QUESTIONS */}
        <section style={{ background: "#eaf2fa" }}>
          <div className="summit-container py-14" style={{ maxWidth: 1040 }}>
            <div
              className="grid lg:grid-cols-12 items-center gap-6"
              style={{ background: "#ffffff", border: "1px solid #cfe0f1", padding: "1.75rem 1.75rem" }}
            >
              <div className="lg:col-span-1 flex justify-center lg:justify-start">
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    width: 56, height: 56, background: "#eaf2fa", color: "#1E1968",
                    borderRadius: 999, border: "1px solid #cfe0f1",
                  }}
                >
                  <Headphones style={{ width: 26, height: 26 }} strokeWidth={1.5} />
                </span>
              </div>
              <div className="lg:col-span-7">
                <h2 className="font-serif" style={{ color: "#1E1968", fontSize: "1.4rem", fontWeight: 600, lineHeight: 1.25 }}>
                  Still have questions?
                </h2>
                <p className="mt-2 text-base" style={{ color: "#17233D", lineHeight: 1.65 }}>
                  If you have a question we didn&rsquo;t answer here, please get in touch and we&rsquo;ll be glad to help.
                </p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <a
                  href="mailto:info@summit-rs.com"
                  className="btn-primary"
                  data-testid="faq-contact-cta"
                  aria-label="Email Summit Retirement Solutions at info@summit-rs.com"
                >
                  Contact Support <ArrowRight className="ml-2 w-4 h-4" aria-hidden="true" />
                </a>
              </div>
            </div>

            <p className="mt-6 text-sm text-center" style={{ color: "#3a4d70" }}>
              Read more about{" "}
              <Link to="/how-the-score-works" className="underline" style={{ color: "#1E1968", fontWeight: 500 }}>how your score is calculated</Link>{" "}
              or review our{" "}
              <Link to="/privacy" className="underline" style={{ color: "#1E1968", fontWeight: 500 }}>privacy &amp; data information</Link>.
            </p>
          </div>
        </section>

      </main>
      <MinimalFooter />
    </div>
  );
}
